import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  Loader2,
  Search,
  RefreshCw,
  Package,
  MapPin,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wrench,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';
import { storeApiService, Store, StoreItem } from '../services/storeApi';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { toast } from 'sonner';

export function StoreInventoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [inventory, setInventory] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [issueOpen, setIssueOpen] = useState(false);
  const [issuingItem, setIssuingItem] = useState<StoreItem | null>(null);
  const [issueQuantity, setIssueQuantity] = useState<string>('');
  const [issueUnit, setIssueUnit] = useState<string>('');
  const [issueTransactionType, setIssueTransactionType] = useState<'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT'>('OUT');
  const [issueReferenceType, setIssueReferenceType] = useState<'GRN' | 'SALES' | 'PRODUCTION' | 'TRANSFER' | 'ADJUSTMENT'>('PRODUCTION');
  const [issueReason, setIssueReason] = useState<string>('');
  const [issueDate, setIssueDate] = useState<string>('');
  const [issueNotes, setIssueNotes] = useState<string>('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setIssueDate(today);
  }, []);

  // Fetch stores
  const fetchStores = useCallback(async () => {
    try {
      setLoading(true);
      const branchId = selectedBranch ?? user?.defaultBranchId;
      if (!branchId) {
        // console.warn('Branch information not available');
        setStores([]);
        return;
      }
      const response = await storeApiService.getStoresByBranch(branchId);
      setStores(response);
    } catch (error) {
      // console.error('Error fetching stores:', error);
      toast.error('Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, user?.defaultBranchId]);

  useEffect(() => {
    if (selectedBranch ?? user?.defaultBranchId) {
      fetchStores();
    }
  }, [fetchStores, selectedBranch, user?.defaultBranchId]);

  // Fetch inventory for selected store
  const fetchInventory = useCallback(async () => {
    if (!selectedStoreId) return;
    
    try {
      setInventoryLoading(true);
      // Fetch a large page and paginate client-side
      const fetchPageSize = 5000;
      const response = await storeApiService.getStoreItems(
        Number(selectedStoreId),
        1,
        fetchPageSize
      );
      const items: StoreItem[] = Array.isArray(response.data) ? response.data : [];
      setInventory(items);
    } catch (error) {
      // console.error('Error fetching inventory:', error);
      toast.error('Failed to fetch inventory');
    } finally {
      setInventoryLoading(false);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchInventory();
      const store = stores.find(s => s.id.toString() === selectedStoreId);
      setSelectedStore(store || null);
    }
  }, [selectedStoreId, stores, fetchInventory]);

  // Reset to page 1 whenever store/filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStoreId, debouncedSearchTerm, statusFilter, typeFilter]);

  const handleRefresh = () => {
    if (selectedStoreId) {
      fetchInventory();
    }
  };

  const openIssueDialog = (item: StoreItem) => {
    setIssuingItem(item);
    setIssueQuantity('');
    setIssueUnit(item.unitOfMeasure || '');
    setIssueTransactionType('OUT');
    setIssueReferenceType('PRODUCTION');
    setIssueReason('');
    setIssueNotes('');
    setIssueOpen(true);
  };

  const submitIssue = async () => {
    if (!issuingItem || !selectedStoreId) return;
    const qty = Number(issueQuantity);
    if (!qty || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    if (qty > (issuingItem.currentStock || 0)) {
      toast.error('Quantity cannot exceed current stock');
      return;
    }
    try {
      // Format transaction date to ISO string if it's just a date
      const transactionDate = issueDate 
        ? (issueDate.includes('T') ? issueDate : `${issueDate}T${new Date().toTimeString().split(' ')[0]}`)
        : new Date().toISOString();

      await storeApiService.createStoreTransactionWithStock({
        storeId: Number(selectedStoreId),
        itemId: issuingItem.itemId,
        transactionType: issueTransactionType,
        quantity: qty,
        unitOfMeasure: issueUnit || undefined,
        referenceType: issueReferenceType,
        reason: issueReason || undefined,
        notes: issueNotes || undefined,
        transactionDate: transactionDate,
        processedBy: user?.id,
        createdBy: user?.id,
        updatedBy: null,
        isActive: true
      });
      toast.success('Issued successfully');
      setIssueOpen(false);
      await fetchInventory();
    } catch (e) {
      // console.error(e);
      toast.error('Failed to issue item');
    }
  };

  

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      AVAILABLE: { 
        color: 'bg-green-100 text-green-800', 
        text: 'Available',
        icon: CheckCircle
      },
      LOW_STOCK: { 
        color: 'bg-yellow-100 text-yellow-800', 
        text: 'Low Stock',
        icon: AlertTriangle
      },
      OUT_OF_STOCK: { 
        color: 'bg-red-100 text-red-800', 
        text: 'Out of Stock',
        icon: XCircle
      },
      DAMAGED: { 
        color: 'bg-gray-100 text-gray-800', 
        text: 'Damaged',
        icon: Wrench
      }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.AVAILABLE;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      RAW_MATERIAL: { color: 'bg-blue-100 text-blue-800', text: 'Raw Material' },
      FINISHED_GOOD: { color: 'bg-purple-100 text-purple-800', text: 'Finished Good' }
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.RAW_MATERIAL;
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  const getStockTrend = (current: number, minimum: number, maximum: number) => {
    if (current <= minimum) {
      return { icon: TrendingDown, color: 'text-red-500', text: 'Critical' };
    } else if (current <= minimum * 1.5) {
      return { icon: AlertTriangle, color: 'text-yellow-500', text: 'Low' };
    } else if (current >= maximum * 0.9) {
      return { icon: TrendingUp, color: 'text-green-500', text: 'High' };
    } else {
      return { icon: CheckCircle, color: 'text-green-500', text: 'Normal' };
    }
  };

  // Filter inventory based on search and filters
  const filteredInventory = inventory.filter(item => {
    const itemName = item.rawItem?.itemName || item.itemName || '';
    const matchesSearch = !debouncedSearchTerm || 
      itemName.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || item.status === statusFilter;
    const matchesType = !typeFilter || item.itemType === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredInventory.length / pageSize));
  }, [filteredInventory.length, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pagedInventory = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredInventory.slice(start, start + pageSize);
  }, [filteredInventory, currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
              Store Inventory
            </h1>
            <p className="text-gray-600 mt-1">View and manage inventory across stores</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
              disabled={!selectedStoreId}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Store Selection */}
        <Card className="border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <CardTitle className="flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Select Store
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="store-select" className="text-sm font-medium text-gray-700 mb-2 block">
                  Choose a store to view inventory
                </Label>
                <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white">
                    <SelectValue placeholder="Select a store..." />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((store) => (
                      <SelectItem key={store.id} value={store.id.toString()}>
                        <div className="flex items-center">
                          <Package className="h-4 w-4 mr-2 text-gray-400" />
                          <div>
                            <div className="font-medium">{store.storeName}</div>
                            <div className="text-sm text-gray-500">{store.storeCode} • {store.storeType}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedStore && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900">{selectedStore.storeName}</h3>
                  <p className="text-sm text-gray-600">{selectedStore.storeCode}</p>
                  <p className="text-sm text-gray-500">{selectedStore.storeType}</p>
                  {selectedStore.location && (
                    <p className="text-sm text-gray-500">{selectedStore.location}</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Inventory Table */}
        {selectedStoreId && (
          <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Package className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-semibold">Inventory</CardTitle>
                    <p className="text-gray-600 text-sm">
                      {selectedStore?.storeName} • {filteredInventory.length}{' '}
                      {filteredInventory.length === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                </div>
                <Badge className="bg-white/20 text-white border-white/30">
                  <Package className="h-3 w-3 mr-1" />
                  {filteredInventory.length}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="p-6">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search inventory..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-48 border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RAW_MATERIAL">Raw Material</SelectItem>
                      <SelectItem value="FINISHED_GOOD">Finished Good</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48 border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AVAILABLE">Available</SelectItem>
                      <SelectItem value="LOW_STOCK">Low Stock</SelectItem>
                      <SelectItem value="OUT_OF_STOCK">Out of Stock</SelectItem>
                      <SelectItem value="DAMAGED">Damaged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Table */}
              {inventoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                  <p className="text-gray-500">Loading inventory...</p>
                </div>
              ) : filteredInventory.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No inventory found</h3>
                  <p className="text-gray-500">
                    {searchTerm || statusFilter || typeFilter 
                      ? 'No items match your current filters.' 
                      : 'This store has no inventory items yet.'
                    }
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                          <TableHead className="font-semibold text-gray-800">Item</TableHead>
                          <TableHead className="font-semibold text-gray-800">Type</TableHead>
                          <TableHead className="font-semibold text-gray-800">Current Stock</TableHead>
                          {/* <TableHead className="font-semibold text-gray-800">Min/Max</TableHead> */}
                          <TableHead className="font-semibold text-gray-800">Unit</TableHead>
                          <TableHead className="font-semibold text-gray-800">Location</TableHead>
                          {/* <TableHead className="font-semibold text-gray-800">Status</TableHead>
                          <TableHead className="font-semibold text-gray-800">Trend</TableHead> */}
                          <TableHead className="font-semibold text-gray-800">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedInventory.map((item) => {
                          const trend = getStockTrend(item.currentStock, item.minimumStock, item.maximumStock);
                          const TrendIcon = trend.icon;
                          
                          return (
                            <TableRow key={item.id} className="hover:bg-gray-50/50">
                              <TableCell className="text-gray-900">
                                <div>
                                  <div className="font-medium">{item.rawItem?.itemName || item.itemName || 'N/A'}</div>
                                  
                                </div>
                              </TableCell>
                              <TableCell>
                                {item.rawItem?.itemCategory?.categoryName || 'N/A'}
                              </TableCell>
                              <TableCell className="text-gray-700">
                                <div className="font-medium text-lg">
                                  {item.currentStock.toLocaleString()}
                                </div>
                              </TableCell>
                              {/* <TableCell className="text-gray-700">
                                <div className="text-sm">
                                  <div>Min: {item.minimumStock.toLocaleString()}</div>
                                  <div>Max: {item.maximumStock.toLocaleString()}</div>
                                </div>
                              </TableCell> */}
                              <TableCell className="text-gray-700">
                                {item.unitOfMeasure || 'N/A'}
                              </TableCell>
                              <TableCell className="text-gray-700">
                                {item.location ? (
                                  <Badge variant="outline" className="border-gray-300">
                                    {item.location}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-500">Not set</span>
                                )}
                              </TableCell>
                              {/* <TableCell>
                                {getStatusBadge(item.status)}
                              </TableCell>
                              <TableCell className="text-gray-700">
                                <div className="flex items-center">
                                  <TrendIcon className={`h-4 w-4 mr-1 ${trend.color}`} />
                                  <span className={`text-sm ${trend.color}`}>{trend.text}</span>
                                </div>
                              </TableCell> */}
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-primary-600 hover:bg-primary-700 text-white"
                                    onClick={() => openIssueDialog(item)}
                                  >
                                    Issue
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-primary-200 text-primary-700 hover:bg-primary-50"
                                    onClick={() => {
                                      if (!selectedStoreId) return;
                                      navigate(`/store-inventory/${selectedStoreId}/item/${item.itemId}/history`, {
                                        state: {
                                          storeName: selectedStore?.storeName,
                                          itemName: item.rawItem?.itemName || item.itemName,
                                        },
                                      });
                                    }}
                                  >
                                    History
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="mt-4 flex items-center justify-end">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* No Store Selected */}
        {!selectedStoreId && !loading && (
          <Card className="border-primary-200 shadow-lg">
            <CardContent className="p-12 text-center">
              <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">Select a Store</h3>
              <p className="text-gray-500 mb-6">
                Choose a store from the dropdown above to view its inventory.
              </p>
            </CardContent>
          </Card>
        )}
        
        <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Issue Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Item</Label>
                <div className="text-gray-900 font-medium">{issuingItem?.rawItem?.itemName || issuingItem?.itemName} {issuingItem?.rawItem?.sku ? `(${issuingItem.rawItem.sku})` : ''}</div>
              </div>
              <div>
                <Label className="text-sm">Current Stock</Label>
                <div className="text-gray-900 font-semibold text-lg">{issuingItem?.currentStock?.toLocaleString() || '0'} {issuingItem?.unitOfMeasure || ''}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input 
                    type="number" 
                    min="0" 
                    max={issuingItem?.currentStock || 0}
                    step="any" 
                    value={issueQuantity} 
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue = Number(value);
                      const maxStock = issuingItem?.currentStock || 0;
                      if (value === '' || (numValue >= 0 && numValue <= maxStock)) {
                        setIssueQuantity(value);
                      }
                    }}
                    className={issueQuantity && Number(issueQuantity) > (issuingItem?.currentStock || 0) ? 'border-red-500' : ''}
                  />
                  {issueQuantity && Number(issueQuantity) > (issuingItem?.currentStock || 0) && (
                    <p className="text-sm text-red-600 mt-1">Quantity cannot exceed current stock</p>
                  )}
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input value={issueUnit} onChange={(e) => setIssueUnit(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Transaction Type *</Label>
                  <Select value={issueTransactionType} onValueChange={(value) => setIssueTransactionType(value as 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT')}>
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select transaction type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IN">IN</SelectItem>
                      <SelectItem value="OUT">OUT</SelectItem>
                      <SelectItem value="TRANSFER">TRANSFER</SelectItem>
                      <SelectItem value="ADJUSTMENT">ADJUSTMENT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference Type *</Label>
                  <Select value={issueReferenceType} onValueChange={(value) => setIssueReferenceType(value as 'GRN' | 'SALES' | 'PRODUCTION' | 'TRANSFER' | 'ADJUSTMENT')}>
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder="Select reference type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GRN">GRN</SelectItem>
                      <SelectItem value="SALES">SALES</SelectItem>
                      <SelectItem value="PRODUCTION">PRODUCTION</SelectItem>
                      <SelectItem value="TRANSFER">TRANSFER</SelectItem>
                      <SelectItem value="ADJUSTMENT">ADJUSTMENT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
                </div>
                <div>
                  <Label>Reason</Label>
                  <Input placeholder="e.g. Production" value={issueReason} onChange={(e) => setIssueReason(e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={3} value={issueNotes} onChange={(e) => setIssueNotes(e.target.value)} />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIssueOpen(false)}>Cancel</Button>
              <Button className="bg-primary-600 hover:bg-primary-700 text-white" onClick={submitIssue}>Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
